import React, { useEffect, useRef, useState } from "react";
import { createDecartClient, models } from "@decartai/sdk";
import { decartToken } from "../Services/ApiServices/chatServices";
import "bootstrap/dist/css/bootstrap.min.css";
import "./Styles/decart-vton.scss";

const CAMERA_STARTUP_TIMEOUT = 10000;
const VIDEO_READY_TIMEOUT = 5000;
const WS_CONNECT_TIMEOUT = 15000;
const REMOTE_STREAM_TIMEOUT = 12000;
const WS_RETRY_ATTEMPTS = 3;
const WS_RETRY_BASE_DELAY = 1500;
const MODEL_FALLBACK_CHAIN = [
  "lucy-vton-latest",
  "lucy-latest",
];

const isWsError = (err) => {
  const msg = err?.message || "";
  const name = err?.name || "";
  return (
    name === "WebSocket error" ||
    msg.includes("WebSocket") ||
    msg.includes("WebSocket error") ||
    msg.includes("socket") ||
    msg.includes("ECONNREFUSED") ||
    msg.includes("NetworkError")
  );
};

const isIOS = () => {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent || "";
  // iPadOS 13+ reports as Mac; detect via touch points as fallback.
  return /iPad|iPhone|iPod/.test(ua) ||
    (ua.includes("Macintosh") && navigator.maxTouchPoints > 1);
};

const isSecureContextAvailable = () => {
  if (typeof window === "undefined") return false;
  if (window.isSecureContext) return true;
  const host = window.location?.hostname || "";
  return host === "localhost" || host === "127.0.0.1" || host === "::1";
};

const isSafari = () => {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent || "";
  return /^((?!chrome|android|crios|fxios).)*safari/i.test(ua) || isIOS();
};

// Reorder m=video payload types so H.264 is preferred; required on Safari where VP8 encoding is unreliable.
const preferH264InSdp = (sdp) => {
  if (!sdp) return sdp;
  const lines = sdp.split(/\r\n/);
  const mVideoIdx = lines.findIndex((l) => l.startsWith("m=video"));
  if (mVideoIdx === -1) return sdp;

  const h264Pts = new Set();
  for (const line of lines) {
    const match = /^a=rtpmap:(\d+)\s+H264\//i.exec(line);
    if (match) h264Pts.add(match[1]);
  }
  if (h264Pts.size === 0) return sdp;

  const mLineParts = lines[mVideoIdx].split(" ");
  const header = mLineParts.slice(0, 3);
  const pts = mLineParts.slice(3);
  const preferred = pts.filter((pt) => h264Pts.has(pt));
  const rest = pts.filter((pt) => !h264Pts.has(pt));
  lines[mVideoIdx] = [...header, ...preferred, ...rest].join(" ");
  return lines.join("\r\n");
};

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

const DecartRealtimeVTON = () => {
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const processedStreamRef = useRef(null);
  const realtimeClientRef = useRef(null);
  const clientRef = useRef(null);
  const startupAbortRef = useRef(false);
  const lastConnectErrorRef = useRef(null);
  const remoteStreamReceivedRef = useRef(false);

  const [isReady, setIsReady] = useState(false);
  const [isCameraOn, setIsCameraOn] = useState(false);
  const [isStarting, setIsStarting] = useState(false);
  const [cameraFacing, setCameraFacing] = useState("user");
  const [prompt, setPrompt] = useState("");
  const [garmentFile, setGarmentFile] = useState(null);
  const [error, setError] = useState(null);
  const [devices, setDevices] = useState({ video: [], audio: [] });

  const getDefaultVTONPrompt = () =>
    "Substitute the current top with a navy blue hoodie with a white cross logo on the chest";

  useEffect(() => {
    console.log("[VTON] mount: initializing Decart + enumerating devices", {
      isIOS: isIOS(),
      isSafari: isSafari(),
      secureContext: isSecureContextAvailable(),
      href: typeof window !== "undefined" ? window.location.href : null,
    });
    initDecart();
    enumerateDevices();
    return () => {
      console.log("[VTON] unmount cleanup");
    };
  }, []);

  const enumerateDevices = async () => {
    console.log("[VTON] enumerateDevices: start");
    try {
      const allDevices = await navigator.mediaDevices.enumerateDevices();
      const video = allDevices.filter((d) => d.kind === "videoinput");
      const audio = allDevices.filter((d) => d.kind === "audioinput");
      console.log("[VTON] enumerateDevices: found", {
        videoCount: video.length,
        audioCount: audio.length,
      });
      setDevices({ video, audio });
    } catch (err) {
      console.warn("[VTON] enumerateDevices failed:", err);
    }
  };

  const initDecart = () => {
    console.log("[VTON] initDecart: requesting token");
    decartToken(
      (response) => {
        const token = response?.data?.client_token;
        console.log("[VTON] initDecart: token received", {
          hasToken: !!token,
          prefix: token?.substring(0, 3),
          length: token?.length,
        });
        if (!token) {
          setError("No Decart token received");
          return;
        }
        clientRef.current = createDecartClient({ apiKey: token });
        console.log("[VTON] initDecart: Decart client created");
      },
      (err) => {
        console.error("[VTON] initDecart: token error", err);
        setError("Failed to initialize Decart client");
      }
    );
  };
  const requestMediaWithTimeout = async (constraints, timeoutMs) => {
    console.log("[VTON] requestMediaWithTimeout: start", { constraints, timeoutMs });
    let timeoutId;
    const timeoutPromise = new Promise((_, reject) => {
      timeoutId = setTimeout(() => {
        console.warn("[VTON] requestMediaWithTimeout: TIMEOUT", timeoutMs);
        reject(
          new Error(
            `Camera startup timed out after ${timeoutMs / 1000}s. The camera may be unavailable, in use, or blocked in this environment.`
          )
        );
      }, timeoutMs);
    });

    try {
      const stream = await Promise.race([
        navigator.mediaDevices.getUserMedia(constraints),
        timeoutPromise,
      ]);
      clearTimeout(timeoutId);
      console.log("[VTON] requestMediaWithTimeout: got stream", {
        tracks: stream.getTracks().map((t) => ({
          kind: t.kind,
          label: t.label,
          readyState: t.readyState,
          enabled: t.enabled,
        })),
      });
      return stream;
    } catch (err) {
      clearTimeout(timeoutId);
      console.error("[VTON] requestMediaWithTimeout: error", err?.name, err?.message);
      throw err;
    }
  };

  const waitForVideoReady = async (videoEl, timeoutMs) => {
    console.log("[VTON] waitForVideoReady: start", { timeoutMs });
    if (!videoEl) {
      throw new Error("Video element is not available");
    }
    return new Promise((resolve, reject) => {
      const timeoutId = setTimeout(() => {
        console.warn("[VTON] waitForVideoReady: TIMEOUT");
        cleanupVideoReadyListeners(videoEl, onLoadedMeta, onCanPlay, onError);
        reject(
          new Error(
            "Video stream did not become ready. The camera may not be delivering frames in this environment."
          )
        );
      }, timeoutMs);

      const onLoadedMeta = async () => {
        console.log("[VTON] waitForVideoReady: loadedmetadata", {
          videoWidth: videoEl.videoWidth,
          videoHeight: videoEl.videoHeight,
        });
        try {
          await videoEl.play();
          console.log("[VTON] waitForVideoReady: play() resolved");
          cleanupVideoReadyListeners(videoEl, onLoadedMeta, onCanPlay, onError);
          clearTimeout(timeoutId);
          resolve();
        } catch (playErr) {
          console.error("[VTON] waitForVideoReady: play() failed", playErr);
          cleanupVideoReadyListeners(videoEl, onLoadedMeta, onCanPlay, onError);
          clearTimeout(timeoutId);
          reject(
            new Error(
              "Video metadata loaded but playback failed: " + playErr.message
            )
          );
        }
      };

      const onCanPlay = () => {
        console.log("[VTON] waitForVideoReady: canplay");
        cleanupVideoReadyListeners(videoEl, onLoadedMeta, onCanPlay, onError);
        clearTimeout(timeoutId);
        resolve();
      };

      const onError = (e) => {
        console.error("[VTON] waitForVideoReady: video element error", e);
        cleanupVideoReadyListeners(videoEl, onLoadedMeta, onCanPlay, onError);
        clearTimeout(timeoutId);
        reject(
          new Error("Video element encountered an error while loading the stream.")
        );
      };

      videoEl.addEventListener("loadedmetadata", onLoadedMeta);
      videoEl.addEventListener("canplay", onCanPlay);
      videoEl.addEventListener("error", onError);
    });
  };

  const cleanupVideoReadyListeners = (videoEl, ...listeners) => {
    videoEl?.removeEventListener("loadedmetadata", listeners[0]);
    videoEl?.removeEventListener("canplay", listeners[1]);
    videoEl?.removeEventListener("error", listeners[2]);
  };

  const connectToDecart = async (stream, initialState) => {
    console.log("[VTON] connectToDecart: start", {
      streamActive: !!stream,
      trackCount: stream?.getTracks().length,
      hasInitialState: !!initialState,
      hasPrompt: !!initialState?.prompt?.text,
      hasImage: !!initialState?.image,
    });
    lastConnectErrorRef.current = null;

    for (let attempt = 0; attempt < WS_RETRY_ATTEMPTS; attempt++) {
      console.log(`[VTON] connectToDecart: attempt ${attempt + 1}/${WS_RETRY_ATTEMPTS}`);
      if (startupAbortRef.current) {
        console.warn("[VTON] connectToDecart: aborted by user");
        throw new Error("Connection cancelled by user");
      }

      for (let modelIdx = 0; modelIdx < MODEL_FALLBACK_CHAIN.length; modelIdx++) {
        const modelName = MODEL_FALLBACK_CHAIN[modelIdx];
        console.log(`[VTON] connectToDecart: trying model ${modelName}`);
        const model = models.realtime(modelName);

        if (startupAbortRef.current) {
          throw new Error("Connection cancelled by user");
        }

        remoteStreamReceivedRef.current = false;

        try {
          console.warn(
            `[DecartRealtimeVTON] Attempt ${attempt + 1}/${WS_RETRY_ATTEMPTS}, model: ${modelName}`
          );

          let connectionTimedOut = false;

          const timeoutPromise = new Promise((_, reject) => {
            setTimeout(() => {
              connectionTimedOut = true;
              reject(
                new Error(
                  `WebSocket connection timed out after ${WS_CONNECT_TIMEOUT / 1000}s`
                )
              );
            }, WS_CONNECT_TIMEOUT);
          });

          const connectPromise = clientRef.current.realtime.connect(stream, {
            model,
            initialState,
            customizeOffer: async (offer) => {
              // Safari's VP8 encoder is unreliable; force H.264 preference in the SDP.
              if (isSafari() && offer?.sdp) {
                const original = offer.sdp;
                const modified = preferH264InSdp(original);
                if (modified !== original) {
                  offer.sdp = modified;
                  console.log("[DecartRealtimeVTON] Applied H.264 preference for Safari");
                }
              }
            },
            onRemoteStream: (editedStream) => {
              console.log(
                "[DecartRealtimeVTON] Remote stream received, tracks:",
                editedStream.getTracks().map((t) => `${t.kind}:${t.readyState}`)
              );
              remoteStreamReceivedRef.current = true;
              processedStreamRef.current = editedStream;
              const videoEl = videoRef.current;
              if (!videoEl) return;
              try {
                videoEl.srcObject = null;
              } catch (_) {
                /* ignore */
              }
              videoEl.srcObject = editedStream;
              videoEl.muted = true;
              videoEl.setAttribute("playsinline", "true");
              videoEl.setAttribute("webkit-playsinline", "true");
              const tryPlay = () =>
                videoEl.play().catch((e) => {
                  console.warn("Remote stream play failed:", e);
                });
              tryPlay();
              videoEl.addEventListener("loadedmetadata", tryPlay, { once: true });
              videoEl.addEventListener("canplay", tryPlay, { once: true });
              setIsReady(true);
            },
          });

          realtimeClientRef.current = await Promise.race([
            connectPromise,
            timeoutPromise,
          ]);

          if (realtimeClientRef.current) {
            // Attach event listeners on the returned client for visibility (esp. Safari).
            const rc = realtimeClientRef.current;
            if (typeof rc.on === "function") {
              rc.on("error", (e) => {
                console.error("[Decart SDK error event]", e?.message || e);
              });
              rc.on("connectionChange", (state) => {
                console.log("[Decart connectionChange]", state);
                if (state === "disconnected" || state === "failed") {
                  setError(
                    "Decart connection lost. Please try again."
                  );
                }
              });
              rc.on("diagnostic", (evt) => {
                if (
                  evt?.name === "peerConnectionStateChange" ||
                  evt?.name === "iceStateChange" ||
                  evt?.name === "phaseTiming" ||
                  evt?.name === "videoStall"
                ) {
                  console.log(`[Decart diag:${evt.name}]`, evt.data);
                }
              });
            }
            console.log(
              `[DecartRealtimeVTON] Connected on ${modelName}, waiting for remote stream...`
            );

            // Watchdog: if the AI processed stream never arrives, treat as failure and try fallback.
            const remoteStreamDeadline = Date.now() + REMOTE_STREAM_TIMEOUT;
            while (
              !remoteStreamReceivedRef.current &&
              Date.now() < remoteStreamDeadline
            ) {
              if (startupAbortRef.current) {
                throw new Error("Connection cancelled by user");
              }
              await sleep(200);
            }

            if (!remoteStreamReceivedRef.current) {
              console.warn(
                `[DecartRealtimeVTON] No remote stream from ${modelName} within ${REMOTE_STREAM_TIMEOUT / 1000}s`
              );
              try {
                realtimeClientRef.current.disconnect();
              } catch (_) {
                /* ignore */
              }
              realtimeClientRef.current = null;
              throw new Error(
                `Decart AI did not send a processed video stream within ${REMOTE_STREAM_TIMEOUT / 1000}s (model: ${modelName})`
              );
            }

            console.log(
              `[DecartRealtimeVTON] Remote stream confirmed on model: ${modelName}`
            );
            return modelName;
          }
        } catch (err) {
          lastConnectErrorRef.current = err;
          console.error(
            `[DecartRealtimeVTON] Connect failed (model: ${modelName}):`,
            err?.message || err
          );

          if (realtimeClientRef.current) {
            try {
              realtimeClientRef.current.disconnect();
            } catch (disconnectErr) {
              console.warn("Error during disconnect cleanup:", disconnectErr);
            }
            realtimeClientRef.current = null;
          }

          const isNoStream = err?.message?.includes("did not send a processed video stream");
          const isRetryable = isWsError(err) || err?.code === "MODEL_NOT_FOUND" || isNoStream;
          if (isRetryable && modelIdx < MODEL_FALLBACK_CHAIN.length - 1) {
            console.warn(
              `[DecartRealtimeVTON] ${err?.code || (isNoStream ? "NO_REMOTE_STREAM" : "WebSocket error")} for model ${modelName}, trying fallback...`
            );
            continue;
          }
        }
      }

      if (startupAbortRef.current) {
        throw new Error("Connection cancelled by user");
      }

      if (attempt < WS_RETRY_ATTEMPTS - 1) {
        const delay =
          WS_RETRY_BASE_DELAY * Math.pow(2, attempt) +
          Math.random() * 500;
        console.warn(
          `[DecartRealtimeVTON] Retrying all models in ${Math.round(delay)}ms...`
        );
        await sleep(delay);
      }
    }

    throw (
      lastConnectErrorRef.current ||
      new Error("Failed to connect to Decart after all retries")
    );
  };

  const startCamera = async () => {
    console.log("[VTON] startCamera: invoked", {
      hasClient: !!clientRef.current,
      cameraFacing,
      isIOS: isIOS(),
      isSafari: isSafari(),
    });
    if (!clientRef.current) {
      console.warn("[VTON] startCamera: Decart client not ready");
      setError("Decart client not initialized");
      return;
    }

    if (!isSecureContextAvailable()) {
      console.warn("[VTON] startCamera: not a secure context");
      setError(
        "Camera access requires a secure context. Please open this page over HTTPS (or localhost)."
      );
      return;
    }

    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      setError(
        "Camera API not available in this browser. On iPhone, please use Safari and ensure the page is served over HTTPS."
      );
      return;
    }

    startupAbortRef.current = false;
    setError(null);
    setIsReady(false);
    setIsStarting(true);

    let stream = null;

    try {
      const model = models.realtime(MODEL_FALLBACK_CHAIN[0]);
      const iOS = isIOS();

      const buildVideoConstraints = (sourceModel) => {
        const constraints = {
          facingMode: cameraFacing,
        };

        // iOS Safari rejects strict WebRTC constraints; use conservative hints only.
        if (iOS) {
          constraints.width = { ideal: 640 };
          constraints.height = { ideal: 480 };
          constraints.frameRate = { ideal: 24, max: 30 };
          return constraints;
        }

        if (Number.isFinite(sourceModel.fps)) {
          constraints.frameRate = { ideal: sourceModel.fps };
        }
        if (Number.isFinite(sourceModel.width)) {
          constraints.width = { ideal: sourceModel.width };
        }
        if (Number.isFinite(sourceModel.height)) {
          constraints.height = { ideal: sourceModel.height };
        }

        return constraints;
      };

      // Video-only avoids iOS's stricter combined camera+mic permission prompts.
      try {
        stream = await requestMediaWithTimeout(
          {
            video: buildVideoConstraints(model),
            audio: false,
          },
          CAMERA_STARTUP_TIMEOUT
        );
      } catch (constraintErr) {
        if (startupAbortRef.current) return;
        if (
          constraintErr?.name === "OverconstrainedError" ||
          constraintErr?.name === "NotFoundError"
        ) {
          console.warn(
            "Constraints too strict, retrying with facingMode only:",
            constraintErr
          );
          stream = await requestMediaWithTimeout(
            {
              video: { facingMode: cameraFacing },
              audio: false,
            },
            CAMERA_STARTUP_TIMEOUT
          );
        } else {
          throw constraintErr;
        }
      }

      if (startupAbortRef.current) {
        if (stream) stream.getTracks().forEach((t) => t.stop());
        return;
      }

      streamRef.current = stream;

      const videoEl = videoRef.current;
      if (videoEl) {
        videoEl.srcObject = stream;
        await waitForVideoReady(videoEl, VIDEO_READY_TIMEOUT);
      }

      if (startupAbortRef.current) {
        if (stream) stream.getTracks().forEach((t) => t.stop());
        streamRef.current = null;
        if (videoEl) videoEl.srcObject = null;
        return;
      }

      let initialState = {
        prompt: {
          text: prompt?.trim()?.length > 0 ? prompt : getDefaultVTONPrompt(),
          enhance: false,
        },
      };

      if (garmentFile) {
        const garmentBlob = new Blob(
          [await garmentFile.arrayBuffer()],
          { type: garmentFile.type }
        );
        initialState.image = garmentBlob;
      }

      await connectToDecart(stream, initialState);
      console.log("[VTON] startCamera: connectToDecart returned");

      if (startupAbortRef.current) {
        if (stream) stream.getTracks().forEach((t) => t.stop());
        streamRef.current = null;
        return;
      }

      if (!realtimeClientRef.current) {
        throw new Error("Failed to connect to Decart realtime");
      }

      console.log("[VTON] startCamera: setup complete, camera is ON");
      setIsCameraOn(true);
      setIsStarting(false);
      await enumerateDevices();
    } catch (err) {
      setIsStarting(false);
      setIsCameraOn(false);
      console.error("Camera error:", err);
      let errorMessage = err?.message || "Failed to start camera";
      if (err?.name === "NotReadableError") {
        errorMessage =
          "Could not start video source. Camera may be in use by another application or unavailable. Close other apps using the camera and try again.";
      } else if (err?.name === "NotAllowedError") {
        errorMessage =
          "Camera access denied. On iPhone, go to Settings > Safari > Camera and allow access, then reload this page.";
      } else if (err?.name === "NotFoundError") {
        errorMessage =
          "No camera found. If you are using a remote desktop or virtual environment, camera passthrough may not be available.";
      } else if (err?.name === "OverconstrainedError") {
        errorMessage =
          "The requested camera settings are not supported on this device. Try switching cameras.";
      } else if (
        err?.name === "TypeError" &&
        err?.message?.includes("insecure")
      ) {
        errorMessage =
          "Camera access blocked: Application must be served over HTTPS.";
      } else if (err?.message?.includes("timed out")) {
        errorMessage =
          "Camera startup timed out. If you are in a remote desktop or virtual session, camera passthrough may not be configured. You can try switching cameras or using a different device.";
      } else if (err?.message?.includes("did not send a processed video stream")) {
        errorMessage =
          "Connected to Decart but no AI video was received. This often means the model is unavailable or WebRTC codecs are incompatible with your browser. Please try again or use a different browser.";
      } else if (isWsError(err)) {
        errorMessage =
          "WebSocket connection to Decart failed. The model may be unavailable in your region, or network/firewall settings may block WebSocket connections. Try a different network or contact support.";
      }
      setError(errorMessage);
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((t) => t.stop());
        streamRef.current = null;
      }
      if (videoRef.current) {
        videoRef.current.srcObject = null;
      }
    }
  };

  const cancelStarting = () => {
    console.log("[VTON] cancelStarting: aborting startup");
    startupAbortRef.current = true;
    setIsStarting(false);
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
    setIsCameraOn(false);
    setIsReady(false);
  };

  const updateOutfit = async () => {
    console.log("[VTON] updateOutfit: start", {
      hasClient: !!realtimeClientRef.current,
      isReady,
      promptLen: prompt?.length,
      hasGarment: !!garmentFile,
    });
    if (!realtimeClientRef.current || !isReady) {
      console.warn("[VTON] updateOutfit: skipped (no client or not ready)");
      return;
    }

    try {
      const updatePayload = {
        prompt: prompt && prompt.trim().length > 0 ? prompt : "",
      };

      if (garmentFile) {
        const garmentBlob = new Blob(
          [await garmentFile.arrayBuffer()],
          { type: garmentFile.type }
        );
        updatePayload.image = garmentBlob;
        console.log("[VTON] updateOutfit: garment blob attached", {
          size: garmentBlob.size,
          type: garmentBlob.type,
        });
      }

      console.log("[VTON] updateOutfit: sending set() to Decart", {
        promptLen: updatePayload.prompt?.length,
        hasImage: !!updatePayload.image,
      });
      await realtimeClientRef.current.set(updatePayload);
      console.log("[VTON] updateOutfit: set() completed");
    } catch (err) {
      console.error("[VTON] updateOutfit: error", err);
      setError(err?.message || "Failed to update outfit");
    }
  };

  const stopCamera = () => {
    console.log("[VTON] stopCamera: start");
    startupAbortRef.current = true;
    remoteStreamReceivedRef.current = false;
    if (realtimeClientRef.current) {
      try {
        realtimeClientRef.current.disconnect();
        console.log("[VTON] stopCamera: Decart client disconnected");
      } catch (err) {
        console.warn("[VTON] stopCamera: disconnect error", err);
      }
      realtimeClientRef.current = null;
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      console.log("[VTON] stopCamera: local tracks stopped");
      streamRef.current = null;
    }
    processedStreamRef.current = null;
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
    setIsCameraOn(false);
    setIsReady(false);
    setIsStarting(false);
  };

  const switchCamera = async () => {
    console.log("[VTON] switchCamera: toggling facingMode", { from: cameraFacing });
    stopCamera();
    setCameraFacing((p) => (p === "user" ? "environment" : "user"));
    await startCamera();
  };

  return (
    <div className="decart-vton-wrapper">
      <video
        ref={videoRef}
        autoPlay
        playsInline
        webkit-playsinline="true"
        muted
      />

      {!isCameraOn && !isStarting && (
        <div className="vton-input-panel">
          <div className="card shadow-lg">
            <div className="card-body">
              <h2 className="text-center mb-3">Virtual Try-On (Realtime)</h2>

              <div className="mb-3">
                <label className="form-label">Garment Reference Image (Optional)</label>
                <input
                  type="file"
                  className="form-control"
                  accept=".jpg,.jpeg,.png,.gif,.webp"
                  onChange={(e) => setGarmentFile(e.target.files[0])}
                />
                {garmentFile && (
                  <small className="text-muted">Selected: {garmentFile.name}</small>
                )}
              </div>

              <div className="mb-3">
                <label className="form-label">Outfit Description</label>
                <input
                  type="text"
                  className="form-control"
                  placeholder="e.g., navy blue hoodie with white logo"
                  value={prompt}
                  onChange={(e) => setPrompt(e.target.value)}
                />
              </div>

              {error && <div className="vton-error">{error}</div>}
              {devices.video.length === 0 && !error && (
                <div className="vton-warning">
                  No camera device detected. If you are in a remote desktop or
                  virtual environment, camera passthrough may not be available.
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <div className="vton-controls">
        {isStarting ? (
          <button className="secondary" onClick={cancelStarting}>
            Cancel
          </button>
        ) : !isCameraOn ? (
          <button className="primary" onClick={startCamera}>
            Open Camera
          </button>
        ) : (
          <>
            <button className="secondary" onClick={switchCamera}>
              Switch
            </button>
            <button
              className="primary"
              onClick={updateOutfit}
              disabled={!isReady}
            >
              Update Outfit
            </button>
            <button className="secondary" onClick={stopCamera}>
              Close
            </button>
          </>
        )}
      </div>

      {isStarting && (
        <p className="vton-loading">Starting camera and connecting to AI...</p>
      )}
      {!isReady && isCameraOn && !isStarting && (
        <p className="vton-loading">Initializing live AI try-on...</p>
      )}
      {isReady && isCameraOn && (
        <div className="vton-live-indicator">
          <div className="vton-live-dot"></div>
          LIVE
        </div>
      )}
    </div>
  );
};

export default DecartRealtimeVTON;
